import type { BillingBasis, FloorType, SeriesFrequency, ServiceFrequency } from '../constants/index.ts';
import type { QuoteContractTerms } from '../types/index.ts';

/**
 * Ready-made starting points for the commercial jobs people actually sell.
 *
 * Picking one fills the quote. Everything it writes is ordinary quote data
 * after that, so the next edit is just editing.
 */

export const COMMERCIAL_PRESET_IDS = ['office', 'medical', 'retail', 'move_out'] as const;

export type CommercialPresetId = (typeof COMMERCIAL_PRESET_IDS)[number];

export interface PresetText {
  en: string;
  es: string;
}

export interface CommercialPresetTask {
  name: PresetText;
  minutes: number;
  frequency: ServiceFrequency;
}

export interface CommercialPresetRoom {
  name: PresetText;
  squareFeet: number | null;
  floorType: FloorType | null;
  /** Heavy for a one-off clean-out. Average for a regular contract. */
  condition: 'average' | 'heavy';
  tasks: CommercialPresetTask[];
}

export interface CommercialPreset {
  id: CommercialPresetId;
  preferredFrequency: SeriesFrequency;
  billingBasis: BillingBasis;
  visitsPerWeek: number | null;
  contract: QuoteContractTerms;
  rooms: CommercialPresetRoom[];
}

function text(en: string, es: string): PresetText {
  return { en, es };
}

function task(
  en: string,
  es: string,
  minutes: number,
  frequency: ServiceFrequency = 'every_visit',
): CommercialPresetTask {
  return { name: text(en, es), minutes, frequency };
}

const YEAR: QuoteContractTerms = {
  termMonths: 12,
  autoRenews: true,
  escalationBasisPoints: 300,
  cancellationNoticeDays: 30,
};

const NONE: QuoteContractTerms = {
  termMonths: null,
  autoRenews: false,
  escalationBasisPoints: null,
  cancellationNoticeDays: null,
};

export const COMMERCIAL_PRESETS: readonly CommercialPreset[] = [
  {
    id: 'office',
    preferredFrequency: 'weekly',
    billingBasis: 'monthly',
    visitsPerWeek: 3,
    contract: YEAR,
    rooms: [
      {
        name: text('Lobby', 'Vestíbulo'),
        squareFeet: 600,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Clean entry glass and doors', 'Limpiar vidrios y puertas de entrada', 5),
          task('Vacuum or mop floors', 'Aspirar o trapear pisos', 10),
          task('Empty trash', 'Vaciar la basura', 3),
        ],
      },
      {
        name: text('Restrooms', 'Baños'),
        squareFeet: 200,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Clean and disinfect fixtures', 'Limpiar y desinfectar sanitarios', 15),
          task('Restock paper and soap', 'Reponer papel y jabón', 4),
          task('Mop floors', 'Trapear pisos', 8),
        ],
      },
      {
        name: text('Work areas', 'Áreas de trabajo'),
        squareFeet: 2000,
        floorType: 'carpet',
        condition: 'average',
        tasks: [
          task('Dust desks and ledges', 'Desempolvar escritorios y repisas', 8),
          task('Vacuum carpet', 'Aspirar alfombra', 12),
          task('Empty trash', 'Vaciar la basura', 5),
          task('Dust high vents and ledges', 'Desempolvar rejillas y repisas altas', 8, 'monthly'),
        ],
      },
      {
        name: text('Break room', 'Comedor'),
        squareFeet: 250,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Wipe counters and sink', 'Limpiar mesadas y lavabo', 8),
          task('Empty trash', 'Vaciar la basura', 3),
        ],
      },
      {
        name: text('Hallways', 'Pasillos'),
        squareFeet: 400,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Mop floors', 'Trapear pisos', 8),
          task('Spot clean handles and switches', 'Limpiar manijas e interruptores', 4),
        ],
      },
    ],
  },
  {
    id: 'medical',
    preferredFrequency: 'weekly',
    billingBasis: 'monthly',
    visitsPerWeek: 5,
    contract: { ...YEAR, cancellationNoticeDays: 60 },
    rooms: [
      {
        name: text('Waiting room', 'Sala de espera'),
        squareFeet: 400,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Disinfect chairs and tables', 'Desinfectar sillas y mesas', 10),
          task('Clean floors', 'Limpiar pisos', 8),
          task('Clean entry glass', 'Limpiar vidrios de entrada', 5),
        ],
      },
      {
        name: text('Exam rooms', 'Consultorios'),
        squareFeet: 800,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Disinfect surfaces', 'Desinfectar superficies', 15),
          task('Clean floors', 'Limpiar pisos', 8),
          task('Empty trash', 'Vaciar la basura', 4),
        ],
      },
      {
        name: text('Restrooms', 'Baños'),
        squareFeet: 180,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Disinfect fixtures', 'Desinfectar sanitarios', 15),
          task('Restock paper and soap', 'Reponer papel y jabón', 4),
          task('Mop floors', 'Trapear pisos', 8),
        ],
      },
      {
        name: text('Staff room', 'Sala del personal'),
        squareFeet: 200,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Wipe counters', 'Limpiar mesadas', 6),
          task('Clean floors', 'Limpiar pisos', 6),
          task('Empty trash', 'Vaciar la basura', 3),
        ],
      },
    ],
  },
  {
    id: 'retail',
    preferredFrequency: 'weekly',
    billingBasis: 'monthly',
    visitsPerWeek: 5,
    contract: YEAR,
    rooms: [
      {
        name: text('Sales floor', 'Piso de ventas'),
        squareFeet: 2500,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Dust mop or vacuum floors', 'Pasar mopa o aspirar pisos', 15),
          task('Dust shelves and fixtures', 'Desempolvar estantes', 8),
          task('Spot clean glass', 'Limpiar vidrios', 5),
        ],
      },
      {
        name: text('Checkout', 'Caja'),
        squareFeet: 150,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Wipe counters', 'Limpiar mesadas', 5),
          task('Empty trash', 'Vaciar la basura', 3),
          task('Clean glass', 'Limpiar vidrios', 4),
        ],
      },
      {
        name: text('Restrooms', 'Baños'),
        squareFeet: 160,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Clean and disinfect fixtures', 'Limpiar y desinfectar sanitarios', 12),
          task('Restock paper and soap', 'Reponer papel y jabón', 4),
          task('Mop floors', 'Trapear pisos', 6),
        ],
      },
      {
        name: text('Back room', 'Depósito'),
        squareFeet: 400,
        floorType: 'concrete',
        condition: 'average',
        tasks: [task('Sweep floors', 'Barrer pisos', 8), task('Empty trash', 'Vaciar la basura', 4)],
      },
      {
        name: text('Entrance', 'Entrada'),
        squareFeet: 120,
        floorType: 'hard',
        condition: 'average',
        tasks: [
          task('Clean entry glass', 'Limpiar vidrios de entrada', 5),
          task('Vacuum mats and mop', 'Aspirar felpudos y trapear', 6),
        ],
      },
    ],
  },
  {
    id: 'move_out',
    preferredFrequency: 'one_time',
    billingBasis: 'per_visit',
    visitsPerWeek: null,
    contract: NONE,
    rooms: [
      {
        name: text('Kitchen', 'Cocina'),
        squareFeet: 150,
        floorType: 'hard',
        condition: 'heavy',
        tasks: [
          task('Clean appliances inside and out', 'Limpiar electrodomésticos por dentro y por fuera', 25),
          task('Wipe counters and cabinets', 'Limpiar mesadas y gabinetes', 15),
          task('Scrub floors', 'Restregar pisos', 12),
        ],
      },
      {
        name: text('Bathrooms', 'Baños'),
        squareFeet: 80,
        floorType: 'hard',
        condition: 'heavy',
        tasks: [
          task('Scrub fixtures', 'Restregar sanitarios', 20),
          task('Clean mirrors and floors', 'Limpiar espejos y pisos', 12),
        ],
      },
      {
        name: text('Living areas', 'Estar'),
        squareFeet: 400,
        floorType: 'mixed',
        condition: 'heavy',
        tasks: [
          task('Dust and wipe surfaces', 'Desempolvar y limpiar superficies', 15),
          task('Clean floors', 'Limpiar pisos', 15),
          task('Clean inside windows', 'Limpiar ventanas por dentro', 15),
        ],
      },
      {
        name: text('Bedrooms', 'Dormitorios'),
        squareFeet: 300,
        floorType: 'carpet',
        condition: 'heavy',
        tasks: [
          task('Dust and wipe', 'Desempolvar y limpiar', 10),
          task('Vacuum or mop floors', 'Aspirar o trapear pisos', 10),
          task('Clean closets', 'Limpiar placares', 10),
        ],
      },
    ],
  },
];

export function commercialPreset(id: CommercialPresetId): CommercialPreset {
  const preset = COMMERCIAL_PRESETS.find((candidate) => candidate.id === id);
  if (!preset) throw new Error(`Unknown commercial preset: ${id}`);
  return preset;
}

/**
 * What a preset writes onto the quote, besides the rooms.
 *
 * A monthly price cannot also offer frequency options. Leaving that switch
 * on, or leaving the tier rows that turn it back on, makes the next save
 * refuse the quote. A monthly preset turns the offer off and clears those
 * rows. A one-off preset leaves them as they were.
 */
export function presetQuoteFields(preset: CommercialPreset): {
  preferredFrequency: SeriesFrequency;
  billingBasis: BillingBasis;
  visitsPerWeek: number | null;
  contract: QuoteContractTerms;
  frequencyTiersEnabled?: false;
  frequencyTiers?: [];
} {
  return {
    preferredFrequency: preset.preferredFrequency,
    billingBasis: preset.billingBasis,
    visitsPerWeek: preset.visitsPerWeek,
    contract: { ...preset.contract },
    ...(preset.billingBasis === 'monthly' ? { frequencyTiersEnabled: false as const, frequencyTiers: [] as [] } : {}),
  };
}

export function presetLabel(text: PresetText, language: string): string {
  return language === 'es' ? text.es : text.en;
}
